import { useMemo } from "react"

import { useAuth } from "shared/lib/useAuth"
import { ProjectPermissions } from "shared/types/__generated__/graphql"

import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { useProjectPermissionsQuery } from "entities/projects/graphql/queries.generated"
import { TeamRole, isTeamRole } from "entities/team-members/types/TeamRole"

import {
  fullProjectPermissions,
  readonlyProjectPermissions
} from "../types/ProjectPermissions"
import { useProjectState } from "./useProjectState"

interface UseProjectPermissionsArgs {
  projectID: string
}

export function useProjectPermissions({
  projectID
}: UseProjectPermissionsArgs): {
  role: TeamRole
  permissions: ProjectPermissions
  isLoading: boolean
} {
  const { isAuthenticated } = useAuth()
  const { isPending } = useProjectState(projectID)
  const isFavoritesProject = projectID === FAVORITES_PROJECT_ID

  const { data: permissionsData, loading: isLoadingPermissions } =
    useProjectPermissionsQuery({
      variables: { projectID },
      skip: isPending || isFavoritesProject || !isAuthenticated
    })

  const projectPermissions = useMemo(() => {
    if (isFavoritesProject) {
      return fullProjectPermissions
    }

    return (
      permissionsData?.projectPermissions?.permissions ||
      readonlyProjectPermissions
    )
  }, [permissionsData, isFavoritesProject, projectID])

  const role = useMemo(() => {
    if (isFavoritesProject) {
      return TeamRole.ADMIN
    }

    const r = permissionsData?.projectPermissions?.role
    return isTeamRole(r) ? r : TeamRole.READ
  }, [permissionsData, isFavoritesProject, projectID])

  return {
    role,
    permissions: projectPermissions,
    isLoading: isLoadingPermissions
  }
}
